import React, { memo, useMemo } from 'react';
import { useAppStore } from '@/stores/appStore';
import { Wall } from '@/services/wallService';

interface WallLayerProps {
  walls: Wall[];
  scale: number;
}

export const WallLayer = memo(({ walls, scale }: WallLayerProps) => {
  const wallsVisible = useAppStore(state => state.rooms.wallsVisible);
  const imageSize = useAppStore(state => state.floorPlan.imageSize);

  // Calculate SVG dimensions with padding
  const svgDimensions = useMemo(() => {
    const svgPadding = 5000;
    const svgWidth = (imageSize.width || 1000) + svgPadding * 2;
    const svgHeight = (imageSize.height || 1000) + svgPadding * 2;
    return { svgPadding, svgWidth, svgHeight };
  }, [imageSize.width, imageSize.height]);

  // Don't render if walls are hidden or no walls exist
  if (!wallsVisible || walls.length === 0) return null;

  return (
    <svg
      viewBox={`${-svgDimensions.svgPadding} ${-svgDimensions.svgPadding} ${svgDimensions.svgWidth} ${svgDimensions.svgHeight}`}
      preserveAspectRatio="none"
      style={{
        position: 'absolute',
        top: `${-svgDimensions.svgPadding}px`,
        left: `${-svgDimensions.svgPadding}px`,
        width: `${svgDimensions.svgWidth}px`,
        height: `${svgDimensions.svgHeight}px`,
        zIndex: 6, // Above rooms (5) but below fixtures (10)
        pointerEvents: 'none', // Walls are non-interactive
      }}
    >
      {walls.map(wall => {
        // Parse points if they're a string
        let points = wall.points;
        if (typeof points === 'string') {
          try {
            points = JSON.parse(points);
          } catch {
            // Failed to parse wall points - skip this wall
            return null;
          }
        }

        // Convert points to SVG path
        const pathData = points
          .map((point, index) => {
            const command = index === 0 ? 'M' : 'L';
            return `${command} ${point.x} ${point.y}`;
          })
          .join(' ');

        // Calibration-ready walls (pixel-perfect) get special visual treatment
        // Cyan/Blue: Calibration-ready walls (pixel-perfect, grid-aligned, suitable for scale calibration)
        // Green (>0.5): High confidence AI detection
        // Yellow (0.3-0.5): Medium confidence detection
        // Orange (<0.3): Low confidence (needs review)
        const isCalibrationReady = wall.calibration_ready || false;

        const getWallColor = (confidence: number, calibrationReady: boolean) => {
          if (calibrationReady) return '#06B6D4'; // Cyan - pixel-perfect calibration walls
          if (confidence >= 0.5) return '#10B981'; // Green
          if (confidence >= 0.3) return '#FBBF24'; // Yellow
          return '#F97316'; // Orange
        };

        const getWallOpacity = (confidence: number, calibrationReady: boolean) => {
          if (calibrationReady) return 1.0; // Maximum opacity for calibration walls
          if (confidence >= 0.5) return 0.9;
          if (confidence >= 0.3) return 0.7;
          return 0.5;
        };

        const wallColor = getWallColor(wall.confidence, isCalibrationReady);
        const wallOpacity = getWallOpacity(wall.confidence, isCalibrationReady);

        return (
          <g key={wall.id}>
            {/* Main wall stroke with confidence-based coloring */}
            <path
              d={pathData}
              fill="none"
              stroke={wallColor}
              strokeWidth={3 / scale}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={wallOpacity}
            />

            {/* White highlight for high confidence or calibration-ready walls */}
            {(wall.confidence >= 0.5 || isCalibrationReady) && (
              <path
                d={pathData}
                fill="none"
                stroke="#ffffff"
                strokeWidth={1 / scale}
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={isCalibrationReady ? 0.6 : 0.4}
              />
            )}
          </g>
        );
      })}
    </svg>
  );
});

WallLayer.displayName = 'WallLayer';
